import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { UnAuthWarningModal } from './UnAuthWarningModal'

interface UnAuthWarningModalContextProps {
  openUnAuthModal: (reason: string) => void
}

const defaultContext: UnAuthWarningModalContextProps = {
  openUnAuthModal: () => {},
}

const UnAuthWarningModalContext =
  createContext<UnAuthWarningModalContextProps>(defaultContext)

export const useUnAuthWarningModal = () => {
  return useContext(UnAuthWarningModalContext)
}

/**
 * Provides one modal for everything below it. `openUnAuthModal` and the context
 * value are memoized because consumers can now number in the dozens — the List
 * View action panel has one per grid row — and an identity change on every
 * render would re-render all of them.
 */
export const UnAuthWarningModalProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [unAuthModalOpenReason, setUnAuthModalOpenReason] = useState('')

  const openUnAuthModal = useCallback((reason: string) => {
    setUnAuthModalOpenReason(reason)
  }, [])

  const closeUnAuthModal = useCallback(() => {
    setUnAuthModalOpenReason('')
  }, [])

  const value = useMemo(() => ({ openUnAuthModal }), [openUnAuthModal])

  return (
    <UnAuthWarningModalContext.Provider value={value}>
      {children}
      {unAuthModalOpenReason && (
        <div data-testid="unAuth-modal">
          <UnAuthWarningModal
            closeUnAuthModal={closeUnAuthModal}
            reason={unAuthModalOpenReason}
          />
        </div>
      )}
    </UnAuthWarningModalContext.Provider>
  )
}

// with HOC pattern is here during migration from auction listing to SuperListing.
// Please try to avoid adding new HOCs and instead use hooks.
export const withUnAuthWarningModal = <P extends object>(
  Component: React.FC<P>
) => {
  const WrappedComponent: React.FC<P> = (props) => (
    <UnAuthWarningModalProvider>
      <Component {...props} />
    </UnAuthWarningModalProvider>
  )

  return WrappedComponent
}
